import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Autocomplete,
  type AutocompleteCloseReason,
  Checkbox,
  CircularProgress,
  Grid,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import type { Property } from 'csstype'
import * as R from 'ramda'
import { useDebounce } from 'use-debounce'
import {
  Defaults,
  type Patient,
  PuiTextField,
  Utils,
} from '@pbt/pbt-ui-components'

import BaseFilter from '~/components/common/filters/BaseFilter'
import Typography from '~/components/elements/Typography/Typography'
import {
  fetchMoreItemsForPatientsList,
  fetchPatientsList,
} from '~/store/actions/patients'
import { getTotalClientCount } from '~/store/reducers/clients'
import {
  getLastFetchIndex,
  getPatientsIsFetchingList,
  getPatientsSearchList,
} from '~/store/reducers/patients'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

const StyledBaseFilter = styled(BaseFilter)(() => ({
  '& .BaseFilter-content': {
    overflowY: 'hidden',
  },
}))

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
    borderBottom: theme.constants.tableBorder,
    padding: theme.spacing(0, 2, 1),
  },
}))

interface PatientFilterProps {
  humanReadable: string
  onChange: ({
    value,
    humanReadable,
  }: {
    humanReadable: string
    value: any[]
  }) => void
  onClose: () => void
  open: boolean
  setSearchTerm: (term: string) => void
  value: any[]
}

const ConversationPatientFilter = ({
  onChange,
  onClose: onCloseProp,
  value: valueProp,
  humanReadable: humanReadableProp,
  open,
  ...rest
}: PatientFilterProps) => {
  const dispatch = useDispatch()
  const patients = useSelector(getPatientsSearchList)
  const isLoading = useSelector(getPatientsIsFetchingList)
  const totalCount = useSelector(getTotalClientCount)
  const lastFetchIndex = useSelector(getLastFetchIndex)

  const { t } = useTranslation(['Communications'])
  const humanReadable = humanReadableProp ? humanReadableProp.split(', ') : []

  const value = valueProp || []

  const [selectedOption, setSelectedOption] = useState(value)
  const [rawInputValue, setRawInputValue] = useState('')
  const [inputValue] = useDebounce(rawInputValue, Defaults.DEBOUNCE_ACTION_TIME)

  const dispatchFetchSearch = (searchTerm: string) => {
    dispatch(
      fetchPatientsList({
        from: 0,
        searchTerm,
        to: Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
      }),
    )
  }
  const selectedOptionIds = R.pluck('id', selectedOption as Array<any>).map(
    (item) => (item === '' ? null : item),
  )

  useEffect(() => {
    const options = value.map((id: string, index: number) => {
      const [name] = (humanReadable[index] || '').split(' ')
      return {
        id,
        name,
      }
    })

    setSelectedOption(options)
  }, [valueProp])

  useEffectExceptOnMount(() => {
    if (open && !R.isNil(inputValue) && inputValue.length > 0) {
      dispatchFetchSearch(inputValue)
    }
  }, [inputValue])

  useEffectExceptOnMount(() => {
    if (open) {
      dispatchFetchSearch(rawInputValue)
    }
  }, [open])

  const apply = () => {
    const selectedValueProp = selectedOptionIds
    const selectedHumanReadableProp = selectedOption
      .map((option: Patient) => option.name)
      .join(', ')

    onChange({
      value: selectedValueProp,
      humanReadable: selectedHumanReadableProp,
    })

    setRawInputValue('')

    onCloseProp()
  }

  const clear = () => {
    setRawInputValue('')
    setSelectedOption([])
  }

  const handleClearOnClose = () => {
    setRawInputValue('')
    if (onCloseProp) {
      onCloseProp()
    }
  }

  const onClose = (
    event: React.SyntheticEvent,
    reason: AutocompleteCloseReason,
  ) => {
    const isLoadingAreaClick = reason === 'blur' && isLoading
    const isSearchBlur = reason === 'blur'
    const isMultipleOptionClick =
      reason === 'selectOption' || reason === 'removeOption'
    if (
      isSearchBlur ||
      isLoadingAreaClick ||
      isMultipleOptionClick ||
      reason === 'toggleInput'
    ) {
      return
    }

    handleClearOnClose()
  }

  const setListboxScrollOnFetched = useCloseAfterCreation(
    (element, scrollTop) => {
      element.scrollTo(0, scrollTop)
    },
    getPatientsIsFetchingList,
  )

  const onListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const listbox = event.currentTarget
    const { scrollTop, clientHeight, scrollHeight } = listbox

    if (scrollTop + clientHeight > scrollHeight - 30) {
      if (totalCount > lastFetchIndex && !isLoading) {
        setListboxScrollOnFetched(listbox, scrollTop)
        dispatch(
          fetchMoreItemsForPatientsList({
            from: lastFetchIndex,
            to: lastFetchIndex + Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
            searchTerm: inputValue,
          }),
        )
      }
    }
  }

  return (
    <StyledBaseFilter
      {...rest}
      contentProps={{
        className: 'BaseFilter-content',
      }}
      hasSelectedOption={Boolean(selectedOption.length)}
      includeSearch={false}
      open={open}
      onApply={apply}
      onClear={clear}
      onClose={onCloseProp}
    >
      <StyledAutocomplete
        autoComplete
        autoHighlight
        disableClearable
        disablePortal
        includeInputInList
        multiple
        open
        ListboxProps={{
          sx: {
            maxHeight: `calc(30vh)`,
            p: 0,
          },
          onScroll: onListboxScroll,
        }}
        forcePopupIcon={false}
        getOptionLabel={(option: any) => option.name}
        inputValue={rawInputValue}
        isOptionEqualToValue={(option: any, currentValue: any) =>
          option.id === currentValue.id
        }
        loading={isLoading}
        options={patients}
        renderInput={(params) => (
          <PuiTextField
            {...params}
            autoFocus
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            label={t('Communications:PATIENTS.SEARCH_PATIENTS')}
            onChange={Utils.handleFormTextInput(setRawInputValue)}
          />
        )}
        renderOption={(props, patient: any, { selected }) => (
          <Grid
            {...props}
            container
            item
            alignItems="center"
            component="li"
            key={patient.id}
            wrap="nowrap"
          >
            <Checkbox checked={selected} size="small" />
            <Typography.ParagraphLg>{patient.name}</Typography.ParagraphLg>
          </Grid>
        )}
        renderTags={() => null}
        slotProps={{
          popper: {
            sx: {
              width: '100% !important',
              position: 'relative !important' as Property.Position,
              transform: 'none !important',
            },
          },
          paper: {
            sx: {
              borderRadius: 0,
              m: 0,
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
          },
        }}
        value={selectedOption}
        onChange={(event, patientValue) =>
          setSelectedOption(patientValue as any[])
        }
        onClose={onClose}
      />
    </StyledBaseFilter>
  )
}

export default ConversationPatientFilter
